import React from 'react';
import PropTypes from 'prop-types';

import {Dialog, DialogActions, DialogContent, DialogTitle} from 'views/components';
import {Button, Dropdown, DropdownMenuItem, IconButton, MultiDropdown, Stack, TextField, Typography} from 'views/components';
import {Add as AddIcon, Delete as DeleteIcon} from 'views/icons';
import {Translate, translate} from 'views/module';

import {displayCode} from 'utils/backend/loom/job/job_adapter';

const JobEditDialog = (props) => {
  const {job, onClose, onSave} = props;

  const startWindows = Object.keys(translate('job.ClientJobs.StartWindows'));
  const days = Object.keys(translate('job.ClientJobs.Days'));
  const levels = Object.keys(translate('job.ClientJobs.EditDialog.Levels'));
  const careNeedOptions = [...new Set([
    ...Object.keys(translate('career.create.CarePlan.Options')),
    ...job.careNeeds,
  ])];
  const languageOptions = [...new Set([
    ...Object.keys(translate('career.create.Requirements.LanguageOptions')),
    ...job.languages.map((language) => language.name),
  ])];

  const [form, setForm] = React.useState({
    summary: job.summary,
    description: job.description,
    additionalConsiderations: job.additionalConsiderations,
    hourlyRate: job.hourlyRate ? String(job.hourlyRate) : '',
    startWindow: job.startWindow || '',
    scheduleSlots: job.scheduleSlots,
    careNeeds: job.careNeeds,
    languages: job.languages,
  });
  const [saving, setSaving] = React.useState(false);

  const slotsValid = form.scheduleSlots.every((slot) => slot.startTime && slot.endTime && slot.startTime !== slot.endTime);
  const languagesValid = form.languages.every((language) => language.name);
  const canSave = form.summary.trim() && form.description.trim() && Number(form.hourlyRate) > 0 &&
    form.startWindow && slotsValid && languagesValid && !saving;

  const setField = (key) => (value) => setForm((current) => ({...current, [key]: value}));

  const setListItem = (list, index, key) => (value) => setForm((current) => ({
    ...current,
    [list]: current[list].map((item, itemIndex) => (itemIndex === index ? {...item, [key]: value} : item)),
  }));

  const addListItem = (list, item) => () => setForm((current) => ({...current, [list]: [...current[list], item]}));

  const removeListItem = (list, index) => () => setForm((current) => ({
    ...current,
    [list]: current[list].filter((item, itemIndex) => itemIndex !== index),
  }));

  const submit = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Dialog onClose={onClose} maxWidth='md'>
      <DialogTitle onClose={onClose}>
        <Translate expression='job.ClientJobs.EditDialog.Title' />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{pt: 1}}>
          <TextField
            fullWidth
            required
            label={translate('job.ClientJobs.EditDialog.Summary')}
            value={form.summary}
            onChange={setField('summary')}
          />
          <TextField
            fullWidth
            required
            multiline
            minRows={14}
            label={translate('job.ClientJobs.EditDialog.Description')}
            value={form.description}
            onChange={setField('description')}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={translate('job.ClientJobs.Considerations')}
            value={form.additionalConsiderations}
            onChange={setField('additionalConsiderations')}
          />
          <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
            <TextField
              fullWidth
              required
              type='number'
              inputProps={{min: 0, step: 0.5}}
              label={translate('job.ClientJobs.EditDialog.HourlyRate')}
              value={form.hourlyRate}
              onChange={setField('hourlyRate')}
            />
            <Dropdown
              required
              label={translate('job.ClientJobs.EditDialog.StartWindow')}
              value={form.startWindow}
              onChange={setField('startWindow')}
            >
              {
                startWindows.map((startWindow) => (
                  <DropdownMenuItem key={startWindow} value={startWindow}>
                    {translate(`job.ClientJobs.StartWindows.${startWindow}`)}
                  </DropdownMenuItem>
                ))
              }
            </Dropdown>
          </Stack>
          <Stack spacing={1.5}>
            <Typography sx={{fontWeight: 600}}>
              <Translate expression='job.ClientJobs.Schedule' />
            </Typography>
            <Typography sx={{fontSize: '13px', color: 'text.secondary'}}>
              <Translate expression='job.ClientJobs.EditDialog.TimezoneNote' />
            </Typography>
            {
              form.scheduleSlots.map((slot, index) => (
                <Stack key={index} direction='row' spacing={1} sx={{alignItems: 'center'}}>
                  <Dropdown
                    label={translate('job.ClientJobs.EditDialog.Day')}
                    value={slot.dayOfWeek}
                    onChange={setListItem('scheduleSlots', index, 'dayOfWeek')}
                  >
                    {
                      days.map((day) => (
                        <DropdownMenuItem key={day} value={day}>
                          {translate(`job.ClientJobs.Days.${day}`)}
                        </DropdownMenuItem>
                      ))
                    }
                  </Dropdown>
                  <TextField
                    fullWidth
                    type='time'
                    InputLabelProps={{shrink: true}}
                    label={translate('job.ClientJobs.EditDialog.StartTime')}
                    value={slot.startTime}
                    onChange={setListItem('scheduleSlots', index, 'startTime')}
                  />
                  <TextField
                    fullWidth
                    type='time'
                    InputLabelProps={{shrink: true}}
                    label={translate('job.ClientJobs.EditDialog.EndTime')}
                    value={slot.endTime}
                    onChange={setListItem('scheduleSlots', index, 'endTime')}
                  />
                  <IconButton
                    aria-label={translate('job.ClientJobs.EditDialog.RemoveSlot')}
                    onClick={removeListItem('scheduleSlots', index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))
            }
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              sx={{alignSelf: 'flex-start'}}
              onClick={addListItem('scheduleSlots', {dayOfWeek: 'MONDAY', startTime: '09:00', endTime: '17:00'})}
            >
              <Translate expression='job.ClientJobs.EditDialog.AddSlot' />
            </Button>
          </Stack>
          <MultiDropdown
            label={translate('job.ClientJobs.CareNeeds')}
            value={form.careNeeds}
            onChange={setField('careNeeds')}
          >
            {
              careNeedOptions.map((careNeed) => (
                <DropdownMenuItem key={careNeed} value={careNeed}>
                  {displayCode(careNeed)}
                </DropdownMenuItem>
              ))
            }
          </MultiDropdown>
          <Stack spacing={1.5}>
            <Typography sx={{fontWeight: 600}}>
              <Translate expression='job.ClientJobs.Languages' />
            </Typography>
            {
              form.languages.map((language, index) => (
                <Stack key={index} direction='row' spacing={1} sx={{alignItems: 'center'}}>
                  <Dropdown
                    label={translate('job.ClientJobs.EditDialog.Language')}
                    value={language.name}
                    onChange={setListItem('languages', index, 'name')}
                  >
                    {
                      languageOptions.map((name) => (
                        <DropdownMenuItem key={name} value={name}>
                          {displayCode(name)}
                        </DropdownMenuItem>
                      ))
                    }
                  </Dropdown>
                  <Dropdown
                    label={translate('job.ClientJobs.EditDialog.Level')}
                    value={language.level}
                    onChange={setListItem('languages', index, 'level')}
                  >
                    {
                      levels.map((level) => (
                        <DropdownMenuItem key={level} value={level}>
                          {translate(`job.ClientJobs.EditDialog.Levels.${level}`)}
                        </DropdownMenuItem>
                      ))
                    }
                  </Dropdown>
                  <IconButton
                    aria-label={translate('job.ClientJobs.EditDialog.RemoveLanguage')}
                    onClick={removeListItem('languages', index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))
            }
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              sx={{alignSelf: 'flex-start'}}
              onClick={addListItem('languages', {name: '', level: 'REQUIRED'})}
            >
              <Translate expression='job.ClientJobs.EditDialog.AddLanguage' />
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose}>
          <Translate expression='generic.Cancel' />
        </Button>
        <Button disabled={!canSave} onClick={submit}>
          <Translate expression='generic.Save' />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

JobEditDialog.propTypes = {
  job: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default React.memo(JobEditDialog);
